/**
 * VirtualBgApplier — Aplica el procesador de fondo virtual al track LiveKit local.
 *
 * IMPORTANTE: Debe renderizarse dentro de <LiveKitRoom> para tener acceso al
 * LocalParticipant. El procesador se aplica al LocalVideoTrack publicado,
 * por lo que el PACIENTE (remoto) también verá el fondo aplicado — esto
 * resuelve el bug del implementación anterior donde el fondo sólo se
 * componía en un canvas local que no se publicaba.
 *
 * Componente "renderless": no genera DOM. Toda la UI del selector vive
 * en VideoCallPageV2; este componente sólo aplica el procesador cuando
 * cambia `selected`.
 */

import { useEffect, useRef } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import type { LocalVideoTrack } from 'livekit-client';
import { useVirtualBackgroundLK } from '../hooks/useVirtualBackgroundLK';
import {
  type BackgroundOption,
  getBackgroundImageUrl,
} from '../hooks/backgroundCatalog';

interface VirtualBgApplierProps {
  selected: BackgroundOption;
  onError?: (msg: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export function VirtualBgApplier({
  selected,
  onError,
  onLoadingChange,
}: VirtualBgApplierProps) {
  const { localParticipant } = useLocalParticipant();
  const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
  const videoTrack = cameraPub?.videoTrack as LocalVideoTrack | undefined;

  const { setBlur, setImage, clear, loading, error } =
    useVirtualBackgroundLK(videoTrack || null);

  // Aplicar cuando cambia la selección. VIDEO-BG-1: si una IMAGEN no se puede
  // cargar/aplicar, se cae a DESENFOQUE para que el médico nunca quede sin fondo.
  useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      if (!videoTrack) return;
      try {
        if (selected.kind === 'none') {
          await clear();
        } else if (selected.kind === 'blur' && typeof selected.blurRadius === 'number') {
          await setBlur(selected.blurRadius);
        } else if (selected.kind === 'image') {
          const url = await getBackgroundImageUrl(selected);
          if (cancelled) return;
          if (!url) { await setBlur(12); return; } // fallback: imagen no disponible
          await setImage(url);
        }
      } catch (e) {
        console.error('[VirtualBgApplier] Error aplicando fondo, cae a desenfoque:', e);
        if (!cancelled) { try { await setBlur(12); } catch { /* noop */ } }
      }
    };
    apply();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.id, videoTrack]);

  // Si el hook reporta error aplicando una IMAGEN, caer a desenfoque (una vez).
  const fallbackRef = useRef<string>('');
  useEffect(() => {
    if (error && selected.kind === 'image' && fallbackRef.current !== selected.id) {
      fallbackRef.current = selected.id;
      setBlur(12).catch(() => { /* noop */ });
    }
  }, [error, selected.kind, selected.id, setBlur]);

  // Propagar estados al padre
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (error) onError?.(error);
  }, [error, onError]);

  return null;
}
